import { create } from 'zustand';
import { useSettingsStore } from './settings-store';
import { useAuthStore } from './auth-store';
import { writeScopedCache } from '@/lib/cache/scoped-cache';

/**
 * Onboarding is driven entirely by the `nstep` field in custom token claims.
 *
 * nstep values:
 *   undefined / < 3  → Steps 1-2 (auth) handled by app naturally. Step 3: auto-show incoming sync settings.
 *   3                → Step 3 done (incoming sync shown). Not yet persisted as 4 → auto-show incoming sync again.
 *   4                → Show step 4 (country code dialog).
 *   5                → Show step 5 (notification contact dialog).
 *   6+               → Onboarding fully complete — nothing to show.
 *
 * The nstep claim is read from token on page load. Each step completion updates
 * the claim via UPDATE_USER_TOKEN_CLAIMS cloud function.
 */

export interface OnboardingStep {
  id: string;
  label: string;
  shortLabel: string;
}

const TIMELINE_STEPS: OnboardingStep[] = [
  { id: 'saas-auth', label: 'CRM Authorization', shortLabel: 'CRM' },
  { id: 'tpa-auth', label: 'Messaging Setup', shortLabel: 'Messaging' },
  { id: 'incoming-sync', label: 'Incoming Sync', shortLabel: 'Sync' },
];

interface OnboardingStore {
  /** Whether to show the timeline bar (steps 1-3) */
  onboardingRequired: boolean;
  isSharedUser: boolean;

  /** The nstep value from token claims (null = not loaded yet) */
  nstep: number | null;

  /** Which full-page dialog to show (null = none) */
  pendingPromptStep: 'country-code' | 'notification' | null;

  // Collected form data
  defaultCountryCode: string;
  notificationContact: string;

  // Actions
  setOnboardingRequired(required: boolean): void;
  setIsSharedUser(shared: boolean): void;
  setNstep(step: number | null): void;
  setPendingPromptStep(step: 'country-code' | 'notification' | null): void;
  setDefaultCountryCode(code: string): void;
  setNotificationContact(contact: string): void;

  /** Update nstep in token claims */
  updateNstep(step: number): Promise<void>;

  /** Dismiss current prompt step, persist data, advance to next */
  dismissPromptStep(): Promise<void>;
}

/** Persist nstep to Firestore preferences and localStorage fallback */
async function persistNstep(nstep: number) {
  // Save to localStorage immediately (survives even if Firestore path not ready)
  try { writeScopedCache('ua_onboarding_nstep', String(nstep)); } catch { /* */ }
  await useSettingsStore.getState().updatePreferences({ nstep } as any);
}

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  onboardingRequired: false,
  isSharedUser: false,
  nstep: null,
  pendingPromptStep: null,
  defaultCountryCode: '',
  notificationContact: '',

  setOnboardingRequired: (required) => set({ onboardingRequired: required }),
  setIsSharedUser: (shared) => set({ isSharedUser: shared }),
  setNstep: (step) => set({ nstep: step }),
  setPendingPromptStep: (step) => set({ pendingPromptStep: step }),
  setDefaultCountryCode: (code) => set({ defaultCountryCode: code }),
  setNotificationContact: (contact) => set({ notificationContact: contact }),

  updateNstep: async (step) => {
    try {
      await persistNstep(step);
      set({ nstep: step });
      console.log(`[Onboarding] nstep updated to ${step}`);
    } catch (err) {
      console.warn('[Onboarding] Failed to update nstep:', err);
    }
  },

  dismissPromptStep: async () => {
    const { pendingPromptStep, defaultCountryCode, notificationContact, nstep } = get();
    const updatePreferences = useSettingsStore.getState().updatePreferences;

    if (pendingPromptStep === 'country-code') {
      // Save country code if entered
      if (defaultCountryCode) {
        await updatePreferences({ defaultCountryCode });
      }
      // Advance claim to nstep=5
      await get().updateNstep(5);
      // Show notification step next
      set({ pendingPromptStep: 'notification' });
    } else if (pendingPromptStep === 'notification') {
      const uid = useAuthStore.getState().user?.uid;
      if (notificationContact) {
        // Save the actual phone to both preferences and the CRM profile.
        await updatePreferences({ notificationContact });
        if (uid) {
          import('@/lib/crm-sync')
            .then(({ syncPhoneNumber }) => syncPhoneNumber(uid, notificationContact))
            .catch((e) => console.warn('[Onboarding] CRM phone sync failed:', e));
        }
      } else if (uid) {
        // Skip with no input — write the 100001 sentinel so the
        // ph-empty trigger in use-app-init doesn't re-ask on the next
        // session. Mirrors legacy ignorePhoneVerifyStepManually.
        import('@/lib/crm-sync')
          .then(({ syncPhoneNumber }) => syncPhoneNumber(uid, '100001'))
          .catch((e) => console.warn('[Onboarding] CRM phone skip sentinel failed:', e));
      }
      // Advance claim to nstep=6 (complete)
      await get().updateNstep(6);
      // Also mark preferences complete
      await updatePreferences({ onboardingComplete: true });
      // Done — clear everything
      set({ pendingPromptStep: null, onboardingRequired: false });
    }
  },
}));

export function getOnboardingSteps(isSharedUser: boolean): OnboardingStep[] {
  return isSharedUser
    ? TIMELINE_STEPS.filter((s) => s.id !== 'incoming-sync')
    : TIMELINE_STEPS;
}
